import { Popconfirm } from "antd"
import PropTypes from "prop-types"
import React, { useEffect } from "react"
import styled from "styled-components"

import Button from "../AppButton"
import StyledAppButton from "../AppButton"

// ----------------------------------------------------------------------------

const KEYBOARD_ESC_KEY_CODE = 27

// ----------------------------------------------------------------------------

function BottomSheet(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const {
        open,
        children,
        onCancel,
        onConfirm,
        title,
        renderExtraButtons,
        loading,
        confirmButtonText,
        onDelete,
    } = props

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    useEffect(() => {
        startESCListener()

        return function cleanup() {
            stopESCListener()
        }

        // eslint-disable-next-line
    }, [])

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleKeyUpEvent(event) {
        if (KEYBOARD_ESC_KEY_CODE === event.keyCode) {
            onCancel && onCancel()
        }
    }

    function startESCListener() {
        window.addEventListener("keyup", handleKeyUpEvent)
    }
    function stopESCListener() {
        window.removeEventListener("keyup", handleKeyUpEvent)
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    if (open) {
        document.body.style.overflow = "hidden"
    } else {
        document.body.style.overflow = ""
    }

    return (
        <div className={`${props.className}${open ? " open" : ""}`}>
            <div className="mask" />

            <div className="sheet">
                <div className="header">
                    <div className="title">{(title && title) || ""}</div>

                    <Button className="close-button" onClick={onCancel} />
                </div>

                {open && <div className="sheet-content">{children}</div>}
                <div className="footer">
                    {onDelete && !loading && (
                        <Popconfirm
                            placement="top"
                            onConfirm={onDelete}
                            title="Sei sicuro di voler eliminare questa risorsa?"
                        >
                            <StyledAppButton
                                htmlType="button"
                                uxType="delete"
                                className="delete-btn"
                            >
                                Elimina
                            </StyledAppButton>
                        </Popconfirm>
                    )}
                    {(onConfirm || loading) && (
                        <StyledAppButton
                            onClick={onConfirm}
                            loading={loading}
                            uxType="ghosted"
                            disabled={!loading}
                        >
                            {confirmButtonText ?? "Conferma"}
                        </StyledAppButton>
                    )}
                </div>
            </div>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

BottomSheet.propTypes = {
    className: PropTypes.string.isRequired,
    open: PropTypes.bool,
    children: PropTypes.any,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    snapPoint: PropTypes.oneOf([40, 50, 80, 100]),
    renderExtraButtons: PropTypes.func,
    loading: PropTypes.bool,
    confirmButtonText: PropTypes.string,
    onDelete: PropTypes.func,
}

BottomSheet.defaultProps = {
    open: false,
    snapPoint: 40,
    renderExtraButtons: () => {},
}

// ----------------------------------------------------------------------------

const StyledBottomSheet = styled(BottomSheet)`
    & {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;

        transform: translateY(100%);
        transition: transform 0.3s ease-in-out;
        overflow-x: hidden;

        z-index: 998;

        ::-webkit-scrollbar {
            display: none;
        }

        .sheet {
            position: fixed;
            top: ${(props) => {
                return (
                    100 -
                    (props?.snapPoint ?? BottomSheet.defaultProps.snapPoint)
                )
            }}vh;
            left: 0;

            display: flex;
            flex-direction: column;

            box-sizing: border-box;
            border-radius: 20px 20px 0 0;

            width: 100vw;
            height: ${(props) =>
                props?.snapPoint ?? BottomSheet.defaultProps.snapPoint}vh;

            background-color: ${(props) =>
                props.theme.colors.primaryBackgroundColor};
            z-index: 999;

            overflow: auto;

            background-color: ${(props) => props.theme.colors.light};

            ::-webkit-scrollbar {
                display: none;
            }

            .header {
                display: flex;
                flex-direction: row;
                border-bottom: 1px solid
                    ${(props) => props.theme.colors.grayBorderDisabled};
                padding: 30px 40px;

                .title {
                    flex: 1;
                    font-size: 18px;
                    font-weight: 600;
                    color: ${(props) => props.theme.colors.textPrimary};
                }

                .close-button {
                    background: transparent;
                    border: 0;
                    padding: 0;
                    width: 20px;
                    height: 20px;
                    &::before {
                        display: block;
                        content: url("/assets/icons/close.svg");
                        width: 20px;
                        height: 20px;
                    }
                }
            }

            .sheet-content {
                flex: 1;
                padding: 30px 40px;
            }

            .footer {
                margin-top: 20px;

                display: flex;
                flex-direction: row;
                justify-content: flex-end;

                .extra-btns {
                    margin-right: 10px;
                }

                .confirm-btn {
                    margin-left: 10px;
                }

                .delete-btn {
                    margin-right: 10px;
                }
            }
        }

        .mask {
            position: fixed;
            opacity: 0;
            transition: opacity 0.1s ease 0.3s;
            z-index: 998;
        }
    }

    &.open {
        transform: translateY(0);

        .mask {
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            transform: scale(1);
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(5px);
        }

        /* MOBILE */

        @media (${(props) => props.theme.breakpoints.smDown}) {
            .drawer {
                min-width: 100vw;
                width: 100vw;
                max-width: 100vw;
                height: 80vh;
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default StyledBottomSheet
